/**
 * ImageWidget - Image display widget
 *
 * Shows an image with configurable aspect ratio.
 * In builder, shows a placeholder if no src is set.
 */

import { memo } from 'react';
import type { ImageWidget as ImageWidgetType } from '@deep-agents/shared-types';
import type { WidgetComponentProps } from '../types';

interface ImageWidgetProps extends Omit<WidgetComponentProps, 'widget'> {
  widget: ImageWidgetType;
}

// Aspect ratio to padding-bottom percentage
const aspectRatioMap: Record<string, string> = {
  '1:1': '100%',
  '4:3': '75%',
  '16:9': '56.25%',
  'auto': '60%', // Default fallback
};

export const ImageWidgetComponent = memo(function ImageWidgetComponent({
  widget,
  isSelected,
  onSelect,
}: ImageWidgetProps) {
  const { props, voice } = widget;
  const { src, alt = 'Image', aspectRatio = '16:9' } = props;

  const paddingBottom = aspectRatioMap[aspectRatio] || aspectRatioMap['auto'];

  return (
    <div
      className={`
        relative rounded-xl overflow-hidden transition-all duration-200 cursor-pointer
        bg-gray-100 border border-gray-200
        ${isSelected ? 'ring-2 ring-violet-500 ring-offset-2' : 'hover:border-gray-300'}
      `}
      onClick={(e) => {
        e.stopPropagation();
        onSelect();
      }}
    >
      {/* Aspect ratio container */}
      <div
        className="relative w-full"
        style={{ paddingBottom }}
      >
        {src ? (
          // Actual image
          <img
            src={src}
            alt={alt}
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          // Placeholder
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
            <svg
              className="w-12 h-12 text-gray-300 mb-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <span className="text-xs text-gray-400">No image selected</span>
            <span className="text-[10px] text-gray-300 mt-1">{aspectRatio}</span>
          </div>
        )}
      </div>

      {/* Voice indicator overlay */}
      {voice?.speakable && (
        <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/50 rounded-full">
          <span className="text-[10px] text-white">🗣️ Speakable</span>
        </div>
      )}
    </div>
  );
});
